import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import NotificationBell from "./Notifications/NotificationBell";
import InstallAppButton from "./Common/InstallAppButton";
import { getProfilePath } from "../utils/storefront";
import { trackUsageEvent } from "./Analytics/CampaignTrafficTracker";

const Navbar = ({ user, userProfile, handleLogout, hideControls = false, publicControls = false }) => {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const isPublicFarmRoute =
    normalizedPath === "/farm" || normalizedPath.startsWith("/farm/");
  const isAuthenticated = Boolean(user?.uid);
  const isPublicExperience = publicControls || isPublicFarmRoute;
  const mobileRef = useRef(null);
  const desktopRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDesktopMenu, setShowDesktopMenu] = useState(false);
  const [expandedMenuGroups, setExpandedMenuGroups] = useState({
    explore: false,
    account: false,
    notificationPrefs: false,
  });
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  const [notifStatus, setNotifStatus] = useState("default");
  const [notifPrefs, setNotifPrefs] = useState({
    chat: true,
    listings: false,
    products: false,
  });

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setNotifStatus(Notification.permission);
    }
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 769px)");
    const handleLayoutChange = (event) => {
      setIsDesktopLayout(event.matches);
      setShowMobileMenu(false);
      setShowDesktopMenu(false);
    };

    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleLayoutChange);

    return () => {
      mediaQuery.removeEventListener("change", handleLayoutChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideMobile =
        !mobileRef.current || !mobileRef.current.contains(event.target);
      const clickedOutsideDesktop =
        !desktopRef.current || !desktopRef.current.contains(event.target);

      if (clickedOutsideMobile && clickedOutsideDesktop) {
        setShowMobileMenu(false);
        setShowDesktopMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchPrefs = async () => {
      if (!user?.uid) return;

      try {
        const snap = await getDoc(doc(db, "users", user.uid));

        if (snap.exists()) {
          const data = snap.data();
          setIsAdminUser(data.admin === true || data.isAdmin === true);

          if (data.notifPrefs) {
            setNotifPrefs(data.notifPrefs);
          }
        }
      } catch (err) {
        console.error("Navbar prefs read failed:", err.code, err.message);
      }
    };

    fetchPrefs();
  }, [user]);

  const requestNotifications = async () => {
    if (typeof Notification === "undefined") {
      alert("This browser does not support notifications.");
      return;
    }

    const permission = await Notification.requestPermission();
    setNotifStatus(permission);
  };

  const fixNotifications = () => {
    alert(
      "To enable notifications:\n\n1. Click the lock icon in your browser bar\n2. Find Notifications\n3. Change to Allow\n4. Refresh the page"
    );
  };

  const updatePrefs = async (newPrefs) => {
    setNotifPrefs(newPrefs);

    if (!user) return;

    try {
      await setDoc(
        doc(db, "users", user.uid),
        { notifPrefs: newPrefs },
        { merge: true }
      );
    } catch (err) {
      console.error("Navbar prefs write failed:", err.code, err.message);
    }
  };

  const isActive = (path) => location.pathname === path;
  const profilePath = isAuthenticated
    ? getProfilePath({ id: user.uid, uid: user.uid, ...(userProfile || {}) })
    : "/home";
  const isProfileRoute =
    isAuthenticated &&
    (location.pathname === profilePath || location.pathname === `/profile/${user.uid}`);
  const isHomeActive =
    location.pathname === "/home" &&
    !["market", "events", "owner"].includes(location.state?.dashboardView);
  const isMarketActive =
    location.pathname === "/marketplace" ||
    (location.pathname === "/home" && location.state?.dashboardView === "market");
  const isPlantsGardenActive =
    isActive("/plants-garden") || isActive("/plants-garden/new");
  const isEventsActive =
    location.pathname === "/events" ||
    (location.pathname === "/home" && location.state?.dashboardView === "events");
  const isMyJobsActive =
    (location.pathname === "/home" || location.pathname === "/jobs") &&
    location.state?.dashboardView === "owner";
  const isJobsActive =
    isActive("/jobs") ||
    (location.pathname === "/home" && ["worker", "owner"].includes(location.state?.dashboardView));
  const isLandShareActive = isActive("/land-share");
  const isFarmHelpActive = isActive("/farm-help");
  const isFarmStandsActive = isActive("/farm-stands") || isActive("/stand");
  const isBrowseUsersActive = isActive("/browse-users") || isActive("/browse");
  const isFarmRecordsActive =
    isActive("/farm-records") ||
    isActive("/records") ||
    isActive("/animal-records");
  const isExploreActive =
    isMarketActive ||
    isFarmStandsActive ||
    isPlantsGardenActive ||
    isEventsActive ||
    isJobsActive ||
    isLandShareActive ||
    isFarmHelpActive ||
    isActive("/map") ||
    isActive("/almanac");
  const isMyFarmActive =
    isActive("/my-farm") ||
    isFarmRecordsActive ||
    isActive("/add-product") ||
    isActive("/add-farm-stand");
  const isAccountActive =
    isProfileRoute ||
    isMyFarmActive ||
    isActive("/contact") ||
    isActive("/admin") ||
    isActive("/content-studio");
  const navbarClassName = [
    "navbar",
    isPublicExperience ? "navbar-public-farm" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const closeMenus = () => {
    setShowMobileMenu(false);
    setShowDesktopMenu(false);
  };

  const getActiveMenuGroups = () => ({
    explore: isExploreActive,
    account: !isExploreActive && isAccountActive,
    notificationPrefs: false,
  });

  const openMenu = (menuSetter) => {
    menuSetter((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        setExpandedMenuGroups(getActiveMenuGroups());
      }
      return nextOpen;
    });
  };

  const toggleMenuGroup = (groupId) => {
    setExpandedMenuGroups((prev) => ({
      explore: groupId === "explore" ? !prev.explore : false,
      account: groupId === "account" ? !prev.account : false,
      notificationPrefs: false,
    }));
  };

  const toggleNotificationPrefs = () => {
    setExpandedMenuGroups((prev) => ({
      ...prev,
      notificationPrefs: !prev.notificationPrefs,
    }));
  };

  const trackPublicAuthClick = (eventType) => {
    trackUsageEvent(eventType, { feature: "public", action: "click" });
  };

  const exploreItems = [
    { label: "Marketplace", to: "/marketplace", active: isMarketActive },
    { label: "Farm Stands / Pickup Spots", to: "/farm-stands", active: isFarmStandsActive },
    { label: "Plants & Garden", to: "/plants-garden", active: isPlantsGardenActive },
    { label: "Events", to: "/events", active: isEventsActive },
    { label: "Jobs & Services", to: "/jobs", active: isJobsActive },
    { label: "Land Share", to: "/land-share", active: isActive("/land-share") },
    { label: "Farm Help", to: "/farm-help", active: isFarmHelpActive },
    { label: "Map", to: "/map", active: isActive("/map") },
    { label: "Almanac", to: "/almanac", active: isActive("/almanac") },
    { label: "Browse Users", to: "/browse-users", active: isBrowseUsersActive },
  ];

  const accountItems = [
    { label: "Profile", to: profilePath, active: isProfileRoute },
    { label: "My Farm", to: "/my-farm", active: isActive("/my-farm") },
    { label: "Farm Records", to: "/farm-records", active: isFarmRecordsActive },
    { label: "Messages", to: "/inbox", active: isActive("/inbox") },
    { label: "Help / Contact Support", to: "/contact", active: isActive("/contact") },
    ...(isAdminUser
      ? [
          { label: "Admin", to: "/admin", active: isActive("/admin") },
          {
            label: "Content Studio",
            to: "/content-studio",
            active: isActive("/content-studio"),
          },
        ]
      : []),
  ];

  const menuEntries = [
    { type: "link", label: "Home", to: "/home", active: isHomeActive },
    { type: "group", id: "explore", title: "Explore", active: isExploreActive, items: exploreItems },
    { type: "link", label: "Community", to: "/community", active: isActive("/community") },
    { type: "group", id: "account", title: "Account", active: isAccountActive, items: accountItems },
  ];

  const renderMenuDropdown = () => (
    <div className="mobile-menu-dropdown">
      <div className="mobile-menu-section">
        {menuEntries.map((entry) => {
          if (entry.type === "link") {
            return (
              <Link
                key={entry.label}
                className={entry.active ? "mobile-menu-link active" : "mobile-menu-link"}
                to={entry.to}
                onClick={closeMenus}
              >
                {entry.label}
              </Link>
            );
          }

          const expanded = expandedMenuGroups[entry.id];
          const sectionContentId = `main-menu-${entry.id}`;

          return (
            <React.Fragment key={entry.id}>
              <button
                type="button"
                className={entry.active ? "mobile-menu-toggle active" : "mobile-menu-toggle"}
                aria-expanded={expanded}
                aria-controls={sectionContentId}
                onClick={() => toggleMenuGroup(entry.id)}
              >
                <span>{entry.title}</span>
                <span aria-hidden="true">{expanded ? "▾" : "›"}</span>
              </button>

              {expanded && (
                <div className="mobile-menu-subsection" id={sectionContentId}>
                  {entry.items.map((item) => (
            <Link
              key={`${entry.title}-${item.label}`}
              className={item.active ? "mobile-menu-link active" : "mobile-menu-link"}
              to={item.to}
              state={item.state}
              onClick={closeMenus}
            >
              {item.label}
            </Link>
                  ))}

                  {entry.id === "account" && (
                    <>
                      {notifStatus === "granted" && (
                        <>
                          <button
                            type="button"
                            className="mobile-menu-toggle"
                            aria-expanded={expandedMenuGroups.notificationPrefs}
                            aria-controls="main-menu-notification-prefs"
                            onClick={toggleNotificationPrefs}
                          >
                            <span>Notification Preferences</span>
                            <span aria-hidden="true">
                              {expandedMenuGroups.notificationPrefs ? "▾" : "›"}
                            </span>
                          </button>

                          {expandedMenuGroups.notificationPrefs && (
                            <div
                              className="mobile-menu-subsection"
                              id="main-menu-notification-prefs"
                            >
                              <label className="notif-option">
                                <input
                                  type="checkbox"
                                  checked={notifPrefs.chat}
                                  onChange={() =>
                                    updatePrefs({
                                      ...notifPrefs,
                                      chat: !notifPrefs.chat,
                                    })
                                  }
                                />
                                <span>Chat messages</span>
                              </label>

                              <label className="notif-option">
                                <input
                                  type="checkbox"
                                  checked={notifPrefs.listings}
                                  onChange={() =>
                                    updatePrefs({
                                      ...notifPrefs,
                                      listings: !notifPrefs.listings,
                                    })
                                  }
                                />
                                <span>New listings</span>
                              </label>

                              <label className="notif-option">
                                <input
                                  type="checkbox"
                                  checked={notifPrefs.products}
                                  onChange={() =>
                                    updatePrefs({
                                      ...notifPrefs,
                                      products: !notifPrefs.products,
                                    })
                                  }
                                />
                                <span>New products</span>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {notifStatus === "default" && (
                        <button
                          type="button"
                          className="mobile-menu-action"
                          onClick={requestNotifications}
                        >
                          Enable Notifications
                        </button>
                      )}

                      {notifStatus === "denied" && (
                        <button type="button" className="mobile-menu-action" onClick={fixNotifications}>
                          Fix Notifications
                        </button>
                      )}

                      <InstallAppButton variant="menu" source="main_menu" />

                      <button
                        type="button"
                        className="mobile-menu-action logout-mobile"
                        onClick={() => {
                          closeMenus();
                          handleLogout();
                        }}
                      >
                        Sign Out
                      </button>
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const renderPublicMenuDropdown = () => (
    <div className="mobile-menu-dropdown public-mobile-menu-dropdown">
      <div className="mobile-menu-section">
        <div className="mobile-menu-section-title">Browse MyFarmingPal</div>
        {[
          ["Home", "/home"],
          ["Marketplace", "/marketplace"],
          ["Farm Stands", "/farm-stands"],
          ["Events", "/events"],
          ["Jobs", "/jobs"],
          ["Plants & Garden", "/plants-garden"],
          ["Land Share", "/land-share"],
          ["Farm Help", "/farm-help"],
          ["Help / Contact Support", "/contact"],
        ].map(([label, to]) => (
          <Link
            key={to}
            className={location.pathname === to ? "mobile-menu-link active" : "mobile-menu-link"}
            to={to}
            onClick={closeMenus}
          >
            {label}
          </Link>
        ))}
      </div>
      <InstallAppButton variant="menu" source="public_mobile_menu" />
      <div className="mobile-menu-section public-mobile-menu-account">
        <Link className="mobile-menu-link" to="/login" onClick={() => { trackPublicAuthClick("signin_click"); closeMenus(); }}>Sign In</Link>
        <Link className="mobile-menu-link public-mobile-menu-join" to="/register" onClick={() => { trackPublicAuthClick("signup_click"); closeMenus(); }}>Join Free</Link>
      </div>
    </div>
  );

  return (
    <header className={navbarClassName}>
      {isPublicExperience && !isAuthenticated && (
        <Link className="public-farm-nav-brand" to="/">
          <span className="public-farm-nav-mark" aria-hidden="true">🌾</span>
          <span>
            <strong>MyFarmingPal</strong>
            <small>Local fields • farm goods • real connections</small>
          </span>
        </Link>
      )}

      {(publicControls || isPublicFarmRoute) && !isAuthenticated && !hideControls && (
        <>
        {!isDesktopLayout && (
          <div className="mobile-menu-wrap public-mobile-menu" ref={mobileRef}>
            <button
              type="button"
              className="mobile-menu-btn"
              aria-expanded={showMobileMenu}
              aria-controls="public-mobile-menu"
              onClick={() => setShowMobileMenu((prev) => !prev)}
            >
              Browse
            </button>
            {showMobileMenu && <div id="public-mobile-menu">{renderPublicMenuDropdown()}</div>}
          </div>
        )}
        <nav className="public-nav-actions" aria-label="Public account links">
          <Link
            className={isHomeActive ? "public-nav-link active" : "public-nav-link"}
            to="/home"
          >
            Home
          </Link>
          <Link
            className={isMarketActive ? "public-nav-link active" : "public-nav-link"}
            to="/marketplace"
          >
            Marketplace
          </Link>
          <Link
            className={isFarmStandsActive ? "public-nav-link active" : "public-nav-link"}
            to="/farm-stands"
          >
            Farm Stands
          </Link>
          <Link
            className={isEventsActive ? "public-nav-link active" : "public-nav-link"}
            to="/events"
          >
            Events
          </Link>
          <Link
            className={isJobsActive ? "public-nav-link active" : "public-nav-link"}
            to="/jobs"
          >
            Jobs
          </Link>
          <Link
            className={isPlantsGardenActive ? "public-nav-link active" : "public-nav-link"}
            to="/plants-garden"
          >
            Plants & Garden
          </Link>
          <Link
            className={isLandShareActive ? "public-nav-link active" : "public-nav-link"}
            to="/land-share"
          >
            Land Share
          </Link>
          <Link
            className={isFarmHelpActive ? "public-nav-link active" : "public-nav-link"}
            to="/farm-help"
          >
            Farm Help
          </Link>
          <Link className="public-nav-link" to="/contact">
            Help / Contact Support
          </Link>
          <Link className="public-nav-link" to="/login" onClick={() => trackPublicAuthClick("signin_click")}>
            Sign In
          </Link>
          <Link className="public-nav-link public-nav-link-primary" to="/register" onClick={() => trackPublicAuthClick("signup_click")}>
            Join Free
          </Link>
        </nav>
        </>
      )}

      {isAuthenticated && !hideControls && (
        <>
          {isDesktopLayout ? (
            <div className="desktop-menu-wrap" ref={desktopRef}>
              <button
                type="button"
                className="mobile-menu-btn desktop-menu-btn"
                aria-expanded={showDesktopMenu}
                aria-controls="desktop-main-menu"
                onClick={() => openMenu(setShowDesktopMenu)}
              >
                Menu
              </button>

              <NotificationBell user={user} />

              {showDesktopMenu && <div id="desktop-main-menu">{renderMenuDropdown()}</div>}
            </div>
          ) : (
            <div className="mobile-menu-wrap" ref={mobileRef}>
              <button
                type="button"
                className="mobile-menu-btn"
                aria-expanded={showMobileMenu}
                aria-controls="mobile-main-menu"
                onClick={() => openMenu(setShowMobileMenu)}
              >
                Menu
              </button>

              <NotificationBell user={user} />

              {showMobileMenu && <div id="mobile-main-menu">{renderMenuDropdown()}</div>}
            </div>
          )}
        </>
      )}
    </header>
  );
};

export default Navbar;
